import { useCallback, useEffect, useState } from "react";
import { CircleChevronLeft } from "lucide-react";
import toast from "react-hot-toast";
import { InverntroyService } from "../services/api/inventroy.service";
import { WholesalerData } from "../components/Inventory-Stocks & Orders/Admin/InventoryManagement/types";

const AddNewWholesalerModal = ({
  onClose,
  selectedWholeSaler,
}: {
  onClose: () => void;
  selectedWholeSaler: WholesalerData;
}) => {
  const [formData, setFormData] = useState({
    name: "",
    mobileNumber: "",
    address: "",
    email: "",
    gstin: "",
    dlNumber: "",
    foodLicNumber: "",
  });
  const [isLoading, setIsLoading] = useState(false);
   const [isDlNumberValid, setIsDlNumberValid] = useState<boolean | null>(null);
  const [isCheckingDlNumber, setIsCheckingDlNumber] = useState<boolean>(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await InverntroyService.addNewWholeSaler(formData);
      if (response.success) {
        toast.success(response.message);
      } else {
        toast.error("Failed to add wholesaler");
      }
    } catch (error) {
        toast.error("Failed to add wholesaler");
    } finally {
      setIsLoading(false);
      onClose();
    }
  };

   const checkDlNumber = useCallback(async (dlNumber: string) => {
    if (!dlNumber.trim()) {
      setIsDlNumberValid(null);
      return;
    }

    setIsCheckingDlNumber(true);
    try {
      const response = await InverntroyService.getExistingWholesaler(dlNumber);
      // Assuming response.isAvailable is true if DL number is available (not taken)
      const isValid = response?.status.status;
      console.log("isValid",isValid,response)
      setIsDlNumberValid(!isValid);
      if (isValid) {
        toast.error(response.message || "This DL number is already in use");
      }
      return !isValid;
    } catch (error) {
      console.error("Error checking DL number:", error);
      toast.error("Failed to validate DL number");
      setIsDlNumberValid(null);
      return false;
    } finally {
      setIsCheckingDlNumber(false);
    }
  }, []);

  const handleDlNumberBlur = async () => {
    await checkDlNumber(formData.dlNumber);
  };

  
  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await InverntroyService.updateWholeSalerDetails(selectedWholeSaler.id, formData);
      if (response.success) {
        toast.success(response.message);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to update wholesaler");
    } finally {
      setIsLoading(false);
      onClose();
    }
  };

  useEffect(() => {
    if (selectedWholeSaler) {
      setFormData({
        name: selectedWholeSaler.name,
        mobileNumber: selectedWholeSaler.mobile_number,
        address: selectedWholeSaler.address,
        email: selectedWholeSaler.email || "",
        gstin: selectedWholeSaler.gstin,
        dlNumber: selectedWholeSaler.dl_number,
        foodLicNumber: selectedWholeSaler.food_lic_number,
      });
    }
  }, []);
  return (
    <div className="flex w-full">
      <div className="w-full">
        <div className="flex items-center mb-4">
          <button
            disabled={isLoading}
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <CircleChevronLeft className="w-6 h-6 bg-[#808080] text-white rounded-full mr-2" />
          </button>
          <h2 className="font-medium text-lg text-emerald-600">
            Add New Wholesaler
          </h2>
        </div>

        <div className="border border-gray-300 bg-white shadow-lg rounded-xl mt-3 p-4">
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-medium mb-2">
              Name
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
              placeholder="Enter Name"
            />
          </div>

          <div className="flex space-x-4 mb-4">
            <div className="w-1/2">
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Phone Number
              </label>
              <input
                type="text"
                name="mobileNumber"
                value={formData.mobileNumber}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                placeholder="Enter Phone Number"
              />
            </div>

            <div className="w-1/2">
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Address
              </label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                placeholder="Enter Address"
              />
            </div>
          </div>

          <div className="flex space-x-4 mb-4">
            <div className="w-1/2">
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Email ID (Optional)
              </label>
              <input
                type="text"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                placeholder="Enter Email"
              />
            </div>

            <div className="w-1/2">
              <label className="block text-gray-700 text-sm font-medium mb-2">
                GSTIN
              </label>
              <input
                type="text"
                name="gstin"
                value={formData.gstin}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                placeholder="Enter Number"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-medium mb-2">
              DL.Number
            </label>
            <div className="relative">
              <input
                type="text"
                name="dlNumber"
                value={formData.dlNumber}
                onChange={handleChange}
                onBlur={handleDlNumberBlur}
                className={`w-full p-2 pr-10 border ${
                  isDlNumberValid === false
                    ? "border-red-500 focus:ring-red-500"
                    : isDlNumberValid === true
                    ? "border-green-500 focus:ring-green-500"
                    : "border-gray-300 focus:ring-green-500"
                } rounded focus:outline-none focus:ring-1`}
                placeholder="Enter Number"
                disabled={isCheckingDlNumber}
              />
              {isCheckingDlNumber && (
                <div className="absolute right-2 top-2.5 h-4 w-4 border-2 border-gray-400 border-t-2 border-t-blue-500 rounded-full animate-spin"></div>
              )}
              {isDlNumberValid === true && !isCheckingDlNumber && (
                <svg
                  className="absolute right-2 top-2.5 h-5 w-5 text-green-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              )}
              {isDlNumberValid === false && !isCheckingDlNumber && (
                <svg
                  className="absolute right-2 top-2.5 h-5 w-5 text-red-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              )}
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-medium mb-2">
              Food LIC Number
            </label>
            <input
              type="text"
              name="foodLicNumber"
              value={formData.foodLicNumber}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
              placeholder="Enter Number"
            />
          </div>

          <div className="flex justify-end space-x-4">
            {selectedWholeSaler ? (
              <button
                onClick={handleUpdate}
                disabled={isLoading}
                className={`px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:bg-gray-200 disabled:cursor-not-allowed`}
              >
                {isLoading ? "Updating..." : "Update Wholesaler"}
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={isLoading}
                className={`px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:bg-gray-200 disabled:cursor-not-allowed`}
              >
                {isLoading ? "Adding..." : "Add Wholesaler"}
              </button>
            )}

            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 w-24"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddNewWholesalerModal;
